import React, { useEffect, useState, useContext } from "react";
import { Tabs, Typography, Tag } from "antd";
import { Parser } from "json2csv";

import "./index.css";
import { DateFormat, errorMessage, getApi } from "../../helpers";
import Badge from "../../components/Badge";
import CardContainer from "../../components/Card";
import TableContainer from "../../components/Table";
import OrderSidebar from "./OrderSidebar";
import ReviewSidebar from "./ReviewSidebar";
import Popup from "../../components/Popup";
import { StoreContext } from "../../context";
import { canWrite, isSuperAdmin } from "../../helpers/permissions";
import IssueSidebar from "./IssueSidebar";

const saveAs = require("file-saver");

export default function Orders() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState("1");

  // Orders state
  const [orderPayload, setOrderPayload] = useState({});
  const [orderTotal, setOrderTotal] = useState(8);
  const [orderList, setOrderList] = useState([]);
  const [orderSidebar, setOrderSidebar] = useState(false);
  const [orderIdSelected, setOrderIdSelected] = useState(0);
  const [orderRowSelected, setOrderRowSelected] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportPopup, setExportPopup] = useState(false);

  // Issues state
  const [issuePayload, setIssuePayload] = useState({});
  const [issueTotal, setIssueTotal] = useState(8);
  const [issueList, setIssueList] = useState([]);
  const [issueSidebar, setIssueSidebar] = useState(false);
  const [issueIdSelected, setIssueIdSelected] = useState(0);

  // Reviews state
  const [reviewPayload, setReviewPayload] = useState({});
  const [reviewTotal, setReviewTotal] = useState(8);
  const [reviewList, setReviewList] = useState([]);
  const [reviewSidebar, setReviewSidebar] = useState(false);
  const [reviewSelected, setReviewSelected] = useState({});

  const getOrderList = async (payload = {}) => {
    setLoading(true);
    if (storeId) payload = { ...payload, store_id: storeId };
    else delete payload.store_id;

    console.log("API Payload:", payload);

    const res = await getApi(payload);
    if (res.data) {
      setOrderList(res.data);
      setOrderTotal(res.meta?.total);
      setLoading(false);
    } else {
      setLoading(false);
    }
    setOrderPayload(payload);
  };

  const getReviewList = async (payload = {}) => {
    setLoading(true);
    if (storeId) payload = { ...payload, store_id: storeId };
    else delete payload.store_id;
    const res = await getApi(payload, "getReview");
    if (res) {
      setReviewList(res.data);
      setReviewTotal(res.meta?.total);
    }
    setReviewPayload(payload);
    setLoading(false);
  };

  const getIssueList = async (payload = {}) => {
    setLoading(true);
    const apiPayload = {};
    if (storeId) apiPayload.store_id = storeId;
    if (payload.keyword) apiPayload.keyword = payload.keyword;
    if (payload.start_date) {
      apiPayload.start_date = payload.start_date;
      apiPayload.end_date = payload.end_date;
    }
    if (payload.filter) apiPayload.status = payload.filter;
    if (payload.page !== undefined) apiPayload.page = payload.page;
    if (payload.size) apiPayload.limit = payload.size;

    const res = await getApi(apiPayload, "listIssuesForDashboard", "ecomm/issue/");
    if (res.data) {
      setIssueList(res.data);
      setIssueTotal(res.meta?.total);
    }
    setIssuePayload(payload);
    setLoading(false);
  };

  // Move column definitions inside the component
  const orderAllColumns = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
    },
    {
      title: "Store",
      dataIndex: "store_name",
      key: "store_name",
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Customer Mobile",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      align: "center",
      render: (text) => <Badge orderStatus={text} />,
      sorter: true,
    },
    {
      title: "Payment Status",
      key: "payment_status",
      dataIndex: "payment_status",
      align: "center",
      render: (text) => <Badge paymentStatus={text} />,
      sorter: true,
    },
    {
      title: "Amount ",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "center",
      sorter: true,
      render: (text) => <div style={{ textAlign: "right" }}>₹ {text}</div>,
    },
    {
      title: "Source",
      dataIndex: "order_source",
      key: "order_source",
      align: "center",
      filters: [
        {
          text: "WhatsApp",
          value: "whatsapp",
        },
        {
          text: "App",
          value: "app",
        },
      ],
      filterMultiple: false,
      onFilter: (value, record) => {
        return record.order_source === value;
      },
      render: (text) => <Badge orderStatus={text} />,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      sorter: true,
      render: (text) => DateFormat(text),
    },
  ];

  const orderMainColumns = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Customer Mobile",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      align: "center",
      render: (text) => <Badge orderStatus={text} />,
      sorter: true,
    },
    {
      title: "Payment Status",
      key: "payment_status",
      dataIndex: "payment_status",
      align: "center",
      render: (text) => <Badge paymentStatus={text} />,
      sorter: true,
    },
    {
      title: "Amount ",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "center",
      sorter: true,
      render: (text) => <div style={{ textAlign: "right" }}>₹ {text}</div>,
    },
    {
      title: "Source",
      dataIndex: "order_source",
      key: "order_source",
      align: "center",
      filters: [
        {
          text: "WhatsApp",
          value: "whatsapp",
        },
        {
          text: "App",
          value: "app",
        },
      ],
      filterMultiple: false, // Only allow single selection
      onFilter: (value, record) => {
        // This is for client-side filtering only
        // Server-side filtering should be handled in TableContainer's onChange
        return record.order_source === value;
      },
      render: (text) => <Badge orderStatus={text} />,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      sorter: true,
      render: (text) => DateFormat(text),
    },
  ];

  const reviewAllColumns = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
    },
    {
      title: "Store",
      dataIndex: "store_name",
      key: "store_name",
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: true,
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      align: "center",
      sorter: true,
      render: (text) => <Badge rating={text} />,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      sorter: true,
      render: (text) => DateFormat(text),
    },
  ];

  const reviewMainColumns = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: true,
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      align: "center",
      sorter: true,
      render: (text) => <Badge rating={text} />,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      sorter: true,
      render: (text) => DateFormat(text),
    },
  ];

  const issueStatusColor = {
    draft: "default",
    awaiting_proof: "orange",
    processing: "blue",
    auto_resolved: "green",
    resolved_by_agent: "green",
    refund_pending: "gold",
    escalated_bot: "purple",
    escalated_agent: "volcano",
    closed: "green",
    rejected: "red",
  };

  const issueColumns = [
    { title: "Issue Id", dataIndex: "issue_id", key: "issue_id", sorter: true },
    { title: "Order Id", dataIndex: "order_id", key: "order_id", sorter: true },
    {
      title: "Customer Name",
      key: "name",
      render: (_, r) => `${r.first_name || ""} ${r.last_name || ""}`.trim(),
    },
    { title: "Mobile", dataIndex: "mobile", key: "mobile" },
    { title: "Issue Type", dataIndex: "issue_type_name", key: "issue_type_name" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (text) => (
        <Tag
          color={issueStatusColor[text] || "default"}
          style={{ textTransform: "capitalize" }}
        >
          {text?.replace(/_/g, " ") || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Total Value",
      dataIndex: "total_value",
      key: "total_value",
      align: "center",
      render: (text) => <div style={{ textAlign: "right" }}>₹ {text}</div>,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      sorter: true,
      render: (text) => DateFormat(text),
    },
  ];

  useEffect(() => {
    if (activeKey === "1") {
      getOrderList(orderPayload);
    }
  }, [storeId, activeKey]);

  const handleTabChange = (key) => {
    setActiveKey(key);
    if (key === "2" && reviewList.length < 1) {
      getReviewList(reviewPayload);
    }
    if (key === "3" && issueList.length < 1) {
      getIssueList(issuePayload);
    }
  };

  const exportOrdersHandler = async () => {
    let payload = {};
    setExporting(true);
    if (orderRowSelected.length > 0) {
      payload = { type: "id", order_id: orderRowSelected };
    } else if (orderPayload.start_date) {
      payload = {
        type: "date",
        start_date: orderPayload.start_date,
        end_date: orderPayload.end_date,
      };
    }
    if (storeId) payload = { ...payload, store_id: storeId };
    const res = await getApi(payload, "exportCsvReport");
    if (res.data && res.data.length > 0) {
      const fieldsHeader = Object.keys(res.data[0]);
      const json2csvParser = new Parser({ fieldsHeader });
      const csv = json2csvParser.parse(res.data);
      const blob = new Blob(["\ufeff", csv]);
      saveAs.saveAs(blob, "export-orders.csv");
      setOrderRowSelected([]);
      setExporting(false);
      setExportPopup(false);
    } else {
      setExporting(false);
      errorMessage("There are no orders for selected date range");
      setExportPopup(false);
    }
  };

  return (
    <div className="card-container">
      <Tabs
        activeKey={activeKey}
        onChange={handleTabChange}
        destroyInactiveTabPane={true}
        type="card"
      >
        <Tabs.TabPane tab="Orders" key="1">
          {exportPopup && (
            <Popup
              saveText="Yes, Export"
              title="Export Orders"
              saveHandler={exportOrdersHandler}
              visible
              width={380}
              loading={exporting}
              cancelHandler={() => setExportPopup(false)}
            >
              <Typography.Text style={{ fontSize: 15, textAlign: "left" }}>
                Are you sure to export orders in a csv file ?
              </Typography.Text>
            </Popup>
          )}
          {orderSidebar && (
            <OrderSidebar
              visible={orderSidebar}
              onClose={() => setOrderSidebar(false)}
              order_id={orderIdSelected}
              onUpdate={() => getOrderList(orderPayload)}
            />
          )}
          <CardContainer bordered={false} hoverable={false}>
            <TableContainer
              breadcrumbTitle="Order Management"
              loading={loading}
              total={orderTotal}
              columns={storeId ? orderMainColumns : orderAllColumns}
              data={orderList}
              dateRangePicker
              searchFor="Order Id, Customer"
              functionsHandler={(payload, tableFilters) => {
                const updatedPayload = { ...payload };

                if (
                  tableFilters?.order_source &&
                  tableFilters.order_source.length > 0
                ) {
                  updatedPayload.order_source = tableFilters.order_source[0];
                }

                getOrderList(updatedPayload);
              }}
              tagFilterOptions={[
                { label: "COD", value: "COD" },
                { label: "Created", value: "order_created" },
                { label: "Paid", value: "order_paid" },
                { label: "Unpaid", value: "order_unpaid" },
                { label: "Delivered", value: "delivered" },
                { label: "Cancelled", value: "order_cancelled" },
              ]}
              rowClickHandler={(record) => (
                setOrderSidebar(true), setOrderIdSelected(record.order_id)
              )}
              // rowSelection={{
              //   type: "checkbox",
              //   selectedRowKeys: orderRowSelected,
              //   onChange: (rowKeys) => setOrderRowSelected(rowKeys),
              // }}
              rowKey={(order) => order.order_id}
              parseButton="export"
              parsing={exporting}
              parseHandler={() =>
                orderPayload.start_date
                  ? setExportPopup(true)
                  : errorMessage("Please select orders to be exported first.")
              }
            />
          </CardContainer>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Reviews" key="2">
          {reviewSidebar && (
            <ReviewSidebar
              visible={reviewSidebar}
              onClose={() => setReviewSidebar(false)}
              data={reviewSelected}
              orderRefHandler={() => {
                setOrderIdSelected(reviewSelected.order_id);
                setOrderSidebar(true);
                setReviewSidebar(false);
              }}
            />
          )}
          <CardContainer bordered={false} hoverable={false}>
            <TableContainer
              breadcrumbTitle="Review Management"
              loading={loading}
              total={reviewTotal}
              columns={storeId ? reviewMainColumns : reviewAllColumns}
              data={reviewList}
              searchFor="Customer, Product"
              rowClickHandler={(record) => (
                setReviewSidebar(true), setReviewSelected(record)
              )}
              functionsHandler={getReviewList}
            />
          </CardContainer>
        </Tabs.TabPane>
        {isSuperAdmin() && (
          <Tabs.TabPane tab="Order Issues" key="3">
            {issueSidebar && (
              <IssueSidebar
                visible={issueSidebar}
                onClose={() => setIssueSidebar(false)}
                issue_id={issueIdSelected}
                onUpdate={() => getIssueList(issuePayload)}
              />
            )}
            <CardContainer bordered={false} hoverable={false}>
              <TableContainer
                breadcrumbTitle="Order Issue Management"
                loading={loading}
                total={issueTotal}
                columns={issueColumns}
                data={issueList}
                dateRangePicker
                searchFor="Issue Id, Customer, Mobile"
                tagFilterOptions={[
                  { label: "Draft", value: "draft" },
                  { label: "Awaiting Proof", value: "awaiting_proof" },
                  { label: "Processing", value: "processing" },
                  { label: "Auto Resolved", value: "auto_resolved" },
                  { label: "Resolved By Agent", value: "resolved_by_agent" },
                  { label: "Refund Pending", value: "refund_pending" },
                  { label: "Escalated Bot", value: "escalated_bot" },
                  { label: "Escalated Agent", value: "escalated_agent" },
                  { label: "Closed", value: "closed" },
                  { label: "Rejected", value: "rejected" },
                ]}
                rowClickHandler={(record) => {
                  setIssueIdSelected(record.issue_id);
                  setIssueSidebar(true);
                }}
                rowKey={(issue) => issue.issue_id}
                functionsHandler={getIssueList}
              />
            </CardContainer>
          </Tabs.TabPane>
        )}
      </Tabs>
    </div>
  );
}
